/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseCrudService, API_CONFIG } from '@nebula-shared/data-access';
import {
  CreatePlanCuentaPayload,
  PageResponsePlanCuenta,
  PlanCuenta,
  PlanCuentaDetail,
  UpdatePlanCuentaPayload,
} from '../../domain/plan-cuentas-modal.models';

@Injectable({ providedIn: 'root' })
export class PlanCuentasService extends BaseCrudService<
  PlanCuenta,
  CreatePlanCuentaPayload,
  UpdatePlanCuentaPayload
> {
  constructor() {
    super();
    const config = API_CONFIG.accounting;
    this.setBaseUrl(`${config.baseUrl}/plan-cuentas-contables`);
  }

  /**
   * Ruta del page-response del plan de cuentas, la que consulta la ventana de busqueda de
   * maestros; el servidor pagina, busca y ordena.
   *
   * @returns la ruta completa del page-response del plan de cuentas
   */
  get rutaDePageResponse(): string {
    return `${this.baseUrl}/page-response`;
  }

  /* eslint-disable max-lines-per-function */
  getPlanCuentasPaginados(
    page = 0,
    size = 10,
    searchTerm = '',
    soloActivos = true,
    tipoPlanContableId?: number,
    tipoPlanCodigo?: number,
    naturaleza?: string,
    soloMovimiento?: boolean,
    soloIva?: boolean,
    soloRetencion?: boolean,
    soloAnticipo?: boolean,
    soloOrdenDebito?: boolean,
    soloOrdenCredito?: boolean,
    soloDeduccion?: boolean,
    ordersBy: { column: string; direction: 'ASC' | 'DESC' }[] = [
      { column: 'codigo', direction: 'ASC' },
    ],
    diferido?: 'S' | 'N',
  ): Observable<PageResponsePlanCuenta> {
    const searchsBy: any[] = [];

    if (soloActivos) {
      searchsBy.push({
        key: 'estadoCuentaContable',
        operation: 'EQUAL',
        value: 'ACTIVO',
        linkOperator: 'AND',
      });
    }

    if (searchTerm && searchTerm.trim() !== '') {
      const cleanTerm = searchTerm.trim();
      const esCodigo = /^\d+$/.test(cleanTerm);

      if (esCodigo) {
        searchsBy.push({
          key: 'codigo',
          operation: 'MATCH_START',
          value: cleanTerm,
          linkOperator: searchsBy.length > 0 ? 'AND' : undefined,
        });
      } else {
        searchsBy.push({
          key: 'nombre',
          operation: 'MATCH',
          value: cleanTerm,
          linkOperator: searchsBy.length > 0 ? 'AND' : undefined,
        });
      }
    }

    if (tipoPlanContableId) {
      searchsBy.push({
        key: 'tipoPlanContableId',
        operation: 'EQUAL',
        value: tipoPlanContableId,
        linkOperator: searchsBy.length > 0 ? 'AND' : undefined,
      });
    }

    if (tipoPlanCodigo) {
      searchsBy.push({
        key: 'tipoPlanCodigo',
        operation: 'EQUAL',
        value: tipoPlanCodigo,
        linkOperator: searchsBy.length > 0 ? 'AND' : undefined,
      });
    }

    if (naturaleza) {
      searchsBy.push({
        key: 'naturaleza',
        operation: 'EQUAL',
        value: naturaleza === 'DEBITO' ? 'DEBITO' : 'CREDITO',
        linkOperator: searchsBy.length > 0 ? 'AND' : undefined,
      });
    }

    // Marca de diferido: Diferidos filtra sus cuentas por tipo con ella (RN-ALC2-29 a 31;
    // Mantis #364). Es opcional: sin ella el buscador no la mira.
    if (diferido) {
      searchsBy.push({
        key: 'diferido',
        operation: 'EQUAL',
        value: diferido,
        linkOperator: searchsBy.length > 0 ? 'AND' : undefined,
      });
    }

    if (soloMovimiento) {
      searchsBy.push({
        key: 'movimiento',
        operation: 'EQUAL',
        value: 'S',
        linkOperator: searchsBy.length > 0 ? 'AND' : undefined,
      });
    }

    if (soloIva) {
      searchsBy.push(
        {
          key: 'cuentaIva',
          operation: 'EQUAL',
          value: 'S',
          linkOperator: searchsBy.length > 0 ? 'AND' : undefined,
        },
        {
          key: 'movimiento',
          operation: 'EQUAL',
          value: 'S',
        },
      );
    }

    if (soloRetencion) {
      searchsBy.push(
        {
          key: 'esRetencion',
          operation: 'EQUAL',
          value: 'S',
          linkOperator: searchsBy.length > 0 ? 'AND' : undefined,
        },
        {
          key: 'movimiento',
          operation: 'EQUAL',
          value: 'S',
        },
      );
    }

    if (soloAnticipo) {
      searchsBy.push(
        {
          key: 'cuentaAnticipo',
          operation: 'EQUAL',
          value: 'S',
          linkOperator: searchsBy.length > 0 ? 'AND' : undefined,
        },
        {
          key: 'movimiento',
          operation: 'EQUAL',
          value: 'S',
        },
      );
    }

    if (soloOrdenDebito) {
      searchsBy.push(
        {
          key: 'movimiento',
          operation: 'EQUAL',
          value: 'S',
          linkOperator: searchsBy.length > 0 ? 'AND' : undefined,
        },
        {
          key: 'codigo',
          operation: 'MATCH_START',
          value: '8',
        },
      );
    }

    if (soloOrdenCredito) {
      searchsBy.push(
        {
          key: 'movimiento',
          operation: 'EQUAL',
          value: 'S',
          linkOperator: searchsBy.length > 0 ? 'AND' : undefined,
        },
        {
          key: 'codigo',
          operation: 'MATCH_START',
          value: '9',
        },
      );
    }

    if (soloDeduccion) {
      searchsBy.push(
        {
          key: 'tipoDeduccion',
          operation: 'EQUAL',
          value: 'RTF',
          linkOperator: searchsBy.length > 0 ? 'AND' : undefined,
        },
        {
          key: 'movimiento',
          operation: 'EQUAL',
          value: 'S',
        },
      );
    }

    for (let i = 0; i < searchsBy.length; i++) {
      if (i < searchsBy.length - 1 && !searchsBy[i].linkOperator) {
        searchsBy[i].linkOperator = 'AND';
      }
    }

    if (searchsBy.length > 0) {
      delete searchsBy[searchsBy.length - 1].linkOperator;
    }

    const body = {
      page: page.toString(),
      size: size.toString(),
      searchsBy: searchsBy,
      ordersBy,
    };

    return this.http.post<any>(`${this.baseUrl}/page-response`, body).pipe(
      map((response) => {
        const rawData = response?.response?.content || response;

        const content = rawData?.content || [];

        const totalElements = Number(rawData?.totalElements || 0);

        const totalPages = rawData?.totalPages || Math.ceil(totalElements / size);

        return {
          content: content,
          totalElements: totalElements,
          totalPages: totalPages,
          size: Number(rawData?.size || size),
          number: Number(rawData?.number || page),
          numberOfElements: content.length,
          first: page === 0,
          last: page >= totalPages - 1,
          empty: content.length === 0,
        } as PageResponsePlanCuenta;
      }),
    );
  }

  buscarPorCodigo(codigo: string): Observable<PlanCuenta | null> {
    const codigoLimpio = codigo.split('-')[0].trim();
    return this.getPlanCuentasPaginados(0, 1, codigoLimpio, true).pipe(
      map((response) => {
        return response.content.length > 0 ? response.content[0] : null;
      }),
    );
  }

  buscarPorNombre(nombre: string): Observable<PlanCuenta | null> {
    const nombreLimpio = nombre.trim();
    return this.getPlanCuentasPaginados(0, 1, nombreLimpio, true).pipe(
      map((response) => {
        return response.content.length > 0 ? response.content[0] : null;
      }),
    );
  }

  buscarPorCodigoExacto(codigo: string): Observable<PlanCuenta | null> {
    const codigoLimpio = codigo.split('-')[0].trim();
    const searchsBy = [
      {
        key: 'estadoCuentaContable',
        operation: 'EQUAL',
        value: 'ACTIVO',
        linkOperator: 'AND',
      },
      {
        key: 'codigo',
        operation: 'EQUAL',
        value: codigoLimpio,
      },
    ];

    const body = {
      page: '0',
      size: '1',
      searchsBy: searchsBy,
      ordersBy: [{ key: 'codigo', direction: 'ASC' }],
    };

    return this.http.post<any>(`${this.baseUrl}/page-response`, body).pipe(
      map((response) => {
        const data = response?.response?.content || response;
        return data?.content?.length > 0 ? data.content[0] : null;
      }),
    );
  }

  getAllPlanCuentas(soloActivos = true, tipoPlanContableId?: number): Observable<PlanCuenta[]> {
    return this.getPlanCuentasPaginados(0, 100, '', soloActivos, tipoPlanContableId).pipe(
      map((firstPage) => {
        const allContent = [...firstPage.content];
        return allContent;
      }),
    );
  }

  getCuentasMovimiento(): Observable<PlanCuenta[]> {
    return this.getPlanCuentasPaginados(
      0,
      100,
      '',
      true,
      undefined,
      undefined,
      undefined,
      true,
    ).pipe(map((response) => response.content));
  }

  getCuentasIva(): Observable<PlanCuenta[]> {
    return this.getPlanCuentasPaginados(
      0,
      100,
      '',
      true,
      undefined,
      undefined,
      undefined,
      undefined,
      true,
    ).pipe(map((response) => response.content));
  }

  getCuentasRetencion(): Observable<PlanCuenta[]> {
    return this.getPlanCuentasPaginados(
      0,
      100,
      '',
      true,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      true,
    ).pipe(map((response) => response.content));
  }

  getCuentasAnticipo(): Observable<PlanCuenta[]> {
    return this.getPlanCuentasPaginados(
      0,
      100,
      '',
      true,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      true,
    ).pipe(map((response) => response.content));
  }

  getActivePlanCuentas(): Observable<PlanCuenta[]> {
    return this.getAllPlanCuentas(true);
  }

  getPaginatedPlanCuentas(
    page = 0,
    size = 10,
    searchTerm?: string,
    sortColumn?: string,
    sortDirection: 'ASC' | 'DESC' = 'ASC',
    naturaleza?: 'Debito' | 'Credito',
    adicionales?: {
      soloMovimiento?: boolean;
      soloIva?: boolean;
      soloRetencion?: boolean;
      soloAnticipo?: boolean;
      soloOrdenDebito?: boolean;
      soloOrdenCredito?: boolean;
      soloDeduccion?: boolean;
      tipoPlanContableId?: number;
      tipoPlanCodigo?: number;
      diferido?: 'S' | 'N';
    },
  ): Observable<PageResponsePlanCuenta> {
    return this.getPlanCuentasPaginados(
      page,
      size,
      searchTerm || '',
      true,
      adicionales?.tipoPlanContableId,
      adicionales?.tipoPlanCodigo,
      naturaleza ? (naturaleza === 'Debito' ? 'DEBITO' : 'CREDITO') : undefined,
      adicionales?.soloMovimiento,
      adicionales?.soloIva,
      adicionales?.soloRetencion,
      adicionales?.soloAnticipo,
      adicionales?.soloOrdenDebito,
      adicionales?.soloOrdenCredito,
      adicionales?.soloDeduccion,
      sortColumn ? [{ column: sortColumn, direction: sortDirection }] : undefined,
      adicionales?.diferido,
    );
  }

  override getAll(): Observable<PlanCuenta[]> {
    return this.http
      .getContent<PlanCuenta[]>(`${this.baseUrl}/read`)
      .pipe(map((response) => response || []));
  }

  override getById(id: number): Observable<PlanCuentaDetail> {
    return this.http.getContent<PlanCuentaDetail>(`${this.baseUrl}/get`, { id: String(id) });
  }

  override create(payload: CreatePlanCuentaPayload): Observable<PlanCuenta> {
    return this.http
      .post<PlanCuenta>(`${this.baseUrl}/create`, payload)
      .pipe(map((response) => response?.response?.content || response));
  }

  override update(id: number, payload: UpdatePlanCuentaPayload): Observable<PlanCuenta> {
    return this.http
      .put<PlanCuenta>(`${this.baseUrl}/update?id=${id}`, payload)
      .pipe(map((response) => response?.response?.content || response));
  }

  override delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/delete?id=${id}`).pipe(map(() => void 0));
  }

  planCuentasporTipo(
    sortColumn: string,
    tipoDeduccion: string,
    page = 0,
    size = 10,
    searchTerm = '',
    sortDirection: 'ASC' | 'DESC' = 'ASC',
  ): Observable<PageResponsePlanCuenta> {
    const searchsBy: any[] = [];

    searchsBy.push(
      {
        key: 'estadoCuentaContable',
        operation: 'EQUAL',
        value: 'ACTIVO',
        linkOperator: 'AND',
      },
      {
        key: 'tipoDeduccion',
        operation: 'EQUAL',
        value: tipoDeduccion,
        linkOperator: 'AND',
      },
      {
        key: 'movimiento',
        operation: 'EQUAL',
        value: 'S',
        linkOperator: 'AND',
      },
    );
    if (searchTerm && searchTerm.trim() !== '') {
      const cleanTerm = searchTerm.trim();
      const esCodigo = /^\d+$/.test(cleanTerm);

      if (esCodigo) {
        searchsBy.push(
          {
            key: 'codigo',
            operation: 'MATCH_START',
            value: cleanTerm,
            linkOperator: 'OR',
          },
          {
            key: 'nombre',
            operation: 'MATCH',
            value: cleanTerm,
            linkOperator: 'OR',
          },
        );
      }
    }

    for (let i = 0; i < searchsBy.length; i++) {
      if (i < searchsBy.length - 1 && !searchsBy[i].linkOperator) {
        searchsBy[i].linkOperator = 'AND';
      }
    }

    if (searchsBy.length > 0) {
      delete searchsBy[searchsBy.length - 1].linkOperator;
    }

    const body = {
      page: page.toString(),
      size: size.toString(),
      searchsBy: searchsBy,
      ordersBy: [{ key: sortColumn, direction: sortDirection ?? 'ASC' }],
    };

    return this.http.post<any>(`${this.baseUrl}/page-response`, body).pipe(
      map((response) => {
        const rawData = response?.response?.content || response;

        const content = rawData?.content || [];

        const totalElements = Number(rawData?.totalElements || 0);
        const totalPages = Number(rawData?.totalPages || Math.ceil(totalElements / size));

        return {
          content: content,
          totalElements: totalElements,
          totalPages: totalPages,
          size: Number(rawData?.size || size),
          number: Number(rawData?.number || page),
          numberOfElements: content.length,
          first: page === 0,
          last: page >= totalPages - 1,
          empty: content.length === 0,
        } as PageResponsePlanCuenta;
      }),
    );
  }
}
